import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Layout from '../components/Layout';
import { patientService } from '../services/patientService';
import { reportService } from '../services/reportService';
import { intakeFormService } from '../services/intakeFormService';
import { clinicalNoteService } from '../services/clinicalNoteService';
import { chartService } from '../services/chartService';
import { assessmentCaseService } from '../services/assessmentCaseService';
import { carePlanService } from '../services/carePlanService';
import { portalService } from '../services/portalService';
import { practiceExtrasService } from '../services/practiceExtrasService';
import { aiService } from '../services/aiService';
import CommentThread from '../components/CommentThread';
import './PatientDetail.css';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [patient, setPatient] = useState(null);
  const [reports, setReports] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [formData, setFormData] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [showEditReportModal, setShowEditReportModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editedReportContent, setEditedReportContent] = useState('');
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [clinicalNotes, setClinicalNotes] = useState([]);
  const [chartOverview, setChartOverview] = useState(null);
  const [assessmentCases, setAssessmentCases] = useState([]);
  const [carePlans, setCarePlans] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const emptyNoteForm = {
    noteType: 'SOAP',
    title: '',
    content: '',
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    data: '',
    intervention: '',
    response: '',
    behavior: '',
  };
  const [noteForm, setNoteForm] = useState(emptyNoteForm);

  useEffect(() => {
    fetchPatientDetails();
    fetchPatientReports();
    fetchPatientIntakeForms();
    fetchClinicalNotes();
    fetchChartOverview();
    fetchAssessmentCases();
    fetchCarePlans();
    fetchLedger();
  }, [id]);

  // Set up WebSocket connection for real-time report status updates
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    console.log('🔌 Setting up WebSocket for PatientDetail, patient ID:', id);

    socket.on('connect', () => {
      console.log('✅ WebSocket connected in PatientDetail');
    });

    // Helper function to check if patient IDs match (handles string/object comparison)
    const isCurrentPatient = (statusPatientId) => {
      if (!statusPatientId) return false;
      // Convert both to strings for comparison
      const currentId = String(id);
      const eventId = String(statusPatientId);
      const matches = currentId === eventId;
      console.log(`🔍 Comparing IDs - Current: "${currentId}", Event: "${eventId}", Match: ${matches}`);
      return matches;
    };

    // Helper function to refresh data
    const refreshPatientData = (reason) => {
      console.log(`🔄 Refreshing patient data - Reason: ${reason}`);
      // Add a small delay to ensure backend has saved the changes
      setTimeout(() => {
        console.log('📡 Fetching fresh data from server...');
        fetchPatientReports();
        fetchPatientIntakeForms();
      }, 1000); // Increased delay to 1 second
    };

    // Listen for report status updates
    socket.on('reportStatus', (status) => {
      console.log('📋 Report Status Update received in PatientDetail:', JSON.stringify(status, null, 2));

      // Always refresh if we get ANY report status update (remove patient ID check temporarily)
      if (status.type === 'complete' || status.type === 'generating' || status.type === 'start') {
        console.log(`✅ Report status: ${status.type} - Refreshing ALL patient data`);
        refreshPatientData(`Report status: ${status.type}`);
      }

      // Also check patient-specific updates
      if (isCurrentPatient(status.patientId)) {
        console.log('✅✅ MATCH! This is for the current patient!');
        refreshPatientData(`Report status for current patient: ${status.type}`);
      }
    });

    // Listen for PDF generation status updates
    socket.on('pdfStatus', (status) => {
      console.log('📄 PDF Status Update received in PatientDetail:', JSON.stringify(status, null, 2));

      // Always refresh for any PDF status
      if (status.type === 'complete' || status.type === 'start') {
        console.log(`✅ PDF status: ${status.type} - Refreshing ALL patient data`);
        refreshPatientData(`PDF status: ${status.type}`);
      }

      // Also check patient-specific updates
      if (isCurrentPatient(status.patientId)) {
        console.log('✅✅ MATCH! This PDF is for the current patient!');
        refreshPatientData(`PDF status for current patient: ${status.type}`);
      }
    });

    // Listen for generic notifications
    socket.on('notification', (notification) => {
      console.log('🔔 Notification received in PatientDetail:', JSON.stringify(notification, null, 2));

      // Refresh if it's related to this patient
      if (isCurrentPatient(notification.patientId)) {
        console.log('✅ Notification is for current patient');
        refreshPatientData('Notification for current patient');
      }

      // Also refresh for any success notification
      if (notification.type === 'success' && notification.message?.toLowerCase().includes('complete')) {
        console.log('✅ Success notification with "complete" - refreshing');
        refreshPatientData('Success notification');
      }
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 Disconnecting WebSocket in PatientDetail');
      socket.off('connect');
      socket.off('reportStatus');
      socket.off('pdfStatus');
      socket.off('notification');
      socket.disconnect();
    };
  }, [id]);

  // Add periodic polling as a backup to ensure data freshness
  useEffect(() => {
    console.log('⏰ Setting up periodic data refresh (every 10 seconds)');
    const pollInterval = setInterval(() => {
      console.log('⏰ Periodic refresh triggered');
      fetchPatientReports();
      fetchPatientIntakeForms();
    }, 10000); // Poll every 10 seconds

    return () => {
      console.log('⏰ Clearing periodic refresh');
      clearInterval(pollInterval);
    };
  }, [id]);

  const fetchPatientDetails = async () => {
    try {
      setLoading(true);
      const data = await patientService.getPatientById(id);
      setPatient(data);
      setFormData({
        fullName: data.fullName || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phoneNumber: data.phoneNumber || '',
        dob: data.dob ? data.dob.split('T')[0] : '',
        age: data.age || '',
        gender: data.gender || '',
        bloodGroup: data.bloodGroup || '',
        address: data.address || '',
        street: data.street || '',
        city: data.city || '',
        state: data.state || '',
        zipCode: data.zipCode || '',
        country: data.country || '',
        emergencyContact: data.emergencyContact || '',
        primaryLanguage: data.primaryLanguage || '',
        raceEthnicity: data.raceEthnicity || '',
        status: data.status || 'active',
      });

      // Set profile image if exists
      if (data.profileImage) {
        setProfilePreview(data.profileImage);
      }
    } catch (error) {
      console.error('Error fetching patient details:', error);
      setError('Failed to load patient details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientReports = async () => {
    try {
      const data = await reportService.getReportsByPatient(id);
      setReports(data || []);
    } catch (error) {
      console.error('Error fetching patient reports:', error);
      setReports([]);
    }
  };

  const fetchPatientIntakeForms = async () => {
    try {
      const data = await intakeFormService.getIntakeFormsByPatient(id);
      setIntakeForms(data || []);
    } catch (error) {
      console.error('Error fetching patient intake forms:', error);
      setIntakeForms([]);
    }
  };

  const fetchClinicalNotes = async () => {
    try {
      const res = await clinicalNoteService.listByPatient(id);
      setClinicalNotes(res.data || []);
    } catch (error) {
      console.error('Error fetching clinical notes:', error);
      setClinicalNotes([]);
    }
  };

  const fetchChartOverview = async () => {
    try {
      const res = await chartService.getOverview(id);
      setChartOverview(res.data || null);
    } catch (error) {
      console.error('Error fetching chart overview:', error);
      setChartOverview(null);
    }
  };

  const fetchAssessmentCases = async () => {
    try {
      const res = await assessmentCaseService.listByPatient(id);
      setAssessmentCases(res.data || []);
    } catch (error) {
      console.error('Error fetching assessment cases:', error);
      setAssessmentCases([]);
    }
  };

  const fetchCarePlans = async () => {
    try {
      const res = await carePlanService.listByPatient(id);
      setCarePlans(res.data || []);
    } catch (error) {
      console.error('Error fetching care plans:', error);
      setCarePlans([]);
    }
  };

  const buildNotePayload = () => {
    const sections = {
      subjective: noteForm.subjective,
      objective: noteForm.objective,
      assessment: noteForm.assessment,
      plan: noteForm.plan,
      data: noteForm.data,
      intervention: noteForm.intervention,
      response: noteForm.response,
      behavior: noteForm.behavior,
    };
    const content =
      noteForm.content ||
      Object.entries(sections)
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n\n');
    return {
      noteType: noteForm.noteType,
      title: noteForm.title || `${noteForm.noteType} note`,
      content,
      sections,
    };
  };

  const handleSaveNote = async () => {
    try {
      const payload = buildNotePayload();
      if (editingNote) {
        await clinicalNoteService.update(editingNote._id, payload);
      } else {
        await clinicalNoteService.create({ patientId: id, ...payload });
      }
      setNoteForm(emptyNoteForm);
      setEditingNote(null);
      setShowNoteModal(false);
      await fetchClinicalNotes();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to save note');
    }
  };

  const fetchLedger = async () => {
    try {
      const res = await practiceExtrasService.getLedger(id);
      setLedger(res.data || null);
    } catch {
      setLedger(null);
    }
  };

  const handleSignNote = async (noteId) => {
    if (!window.confirm('Sign and lock this note? It cannot be edited afterward.')) return;
    const createBilling = window.confirm(
      'Also create invoice + claim draft from AI billing hint?',
    );
    try {
      const res = await clinicalNoteService.sign(noteId, { createBilling: !!createBilling });
      const billing = res.data?.billing || res.billing;
      if (billing?.created) {
        alert('Note signed. Invoice and claim draft created.');
      } else if (billing?.suggestion && !createBilling) {
        const s = billing.suggestion;
        if (
          window.confirm(
            `Suggested CPT: ${s.suggestedCptCode || '—'}\nCreate invoice + claim now?`,
          )
        ) {
          await clinicalNoteService.createBilling(noteId);
        }
      }
      await fetchClinicalNotes();
      await fetchLedger();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to sign note');
    }
  };

  const handleAcceptPayment = async (invoice) => {
    const bal =
      invoice.balanceDueCents ??
      Math.max(0, (invoice.amountCents || 0) - (invoice.amountPaidCents || 0));
    const dollars = window.prompt(
      `Accept payment for ${invoice.invoiceNumber} (balance $${(bal / 100).toFixed(2)}). Amount in dollars:`,
      (bal / 100).toFixed(2),
    );
    if (dollars == null) return;
    const amountCents = Math.round(parseFloat(dollars) * 100);
    if (!amountCents || amountCents <= 0) return;
    const method = window.prompt('Method (cash/card/check/eft):', 'cash') || 'cash';
    try {
      setPayBusy(true);
      await practiceExtrasService.payInvoice(invoice._id, { amountCents, method });
      await fetchLedger();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    } finally {
      setPayBusy(false);
    }
  };

  const handlePayLink = async (invoice) => {
    try {
      setPayBusy(true);
      const res = await practiceExtrasService.createPayLink(invoice._id);
      const url = res.data?.url;
      if (url) {
        window.prompt('Share this Stripe Checkout link with the client:', url);
      } else {
        alert('No pay link returned (check Stripe config).');
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    } finally {
      setPayBusy(false);
    }
  };

  const printStatement = async () => {
    try {
      const res = await practiceExtrasService.getStatement(id);
      const st = res.data || {};
      const money = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
      const rows = (st.invoices || [])
        .map(
          (inv) =>
            `<tr><td>${inv.invoiceNumber}</td><td>${inv.status}</td><td>${money(inv.amountCents)}</td><td>${money(
              inv.balanceDueCents ?? inv.amountCents - (inv.amountPaidCents || 0),
            )}</td></tr>`,
        )
        .join('');
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(`<!doctype html><html><head><title>Statement</title>
        <style>body{font-family:Georgia,serif;padding:32px} table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #ccc;padding:6px;text-align:left} h1{font-size:18px}</style></head><body>
        <h1>Client statement — ${st.patientName || ''}</h1>
        <p>Generated ${st.generatedAt ? new Date(st.generatedAt).toLocaleString() : ''} · Balance ${money(st.balanceCents)}</p>
        <table><thead><tr><th>Invoice</th><th>Status</th><th>Total</th><th>Balance</th></tr></thead>
        <tbody>${rows || '<tr><td colspan=4>No invoices</td></tr>'}</tbody></table>
        <script>window.print()</script></body></html>`);
      w.document.close();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const proposeIntakeFacts = async () => {
    try {
      const intake = intakeForms?.[0];
      if (!intake) {
        alert('No intake form on file for this client.');
        return;
      }
      const res = await aiService.intakeToFacts({
        intake: typeof intake === 'object' ? intake : { text: String(intake) },
        patientId: id,
      });
      const list = res.data?.proposedFacts || res.data?.facts || [];
      if (!Array.isArray(list) || !list.length) {
        alert(res.data?.disclaimer || 'No facts proposed.');
        return;
      }
      const summary = list
        .slice(0, 12)
        .map((f) => `- ${f.type || f.category || 'fact'}: ${f.label || f.name || f.value || JSON.stringify(f)}`)
        .join('\n');
      if (!window.confirm(`AI proposed chart facts (draft — verify before saving):\n\n${summary}\n\nAdd first suggestion as allergy/med/diagnosis?`)) {
        return;
      }
      const first = list[0];
      const type =
        /med/i.test(first.type || first.category || '')
          ? 'medication'
          : /diag/i.test(first.type || first.category || '')
            ? 'diagnosis'
            : 'allergy';
      const label = first.label || first.name || first.value || 'From intake';
      await chartService.createFact(id, {
        type,
        label,
        value: first.value || label,
        source: 'intake_ai_draft',
        status: 'proposed',
      });
      alert(`Added proposed ${type}: ${label}. Review on chart.`);
      await fetchChartOverview();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const handleCreateAssessmentCase = async () => {
    const title = window.prompt('Assessment case title:');
    if (!title) return;
    const referralQuestion = window.prompt('Referral question (optional):') || '';
    const instrumentName = window.prompt('First instrument name (optional, e.g. PHQ-9):') || '';
    try {
      const instruments = instrumentName
        ? [{ name: instrumentName, status: 'pending', respondent: 'client' }]
        : [];
      await assessmentCaseService.create({
        patientId: id,
        title,
        referralQuestion,
        instruments,
      });
      await fetchAssessmentCases();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to create assessment case');
    }
  };

  const handleAddMeasure = async (caseId) => {
    const name = window.prompt('Instrument / measure name (do not enter proprietary items):');
    if (!name) return;
    const respondent = window.prompt('Respondent (client/parent/teacher/clinician):', 'client') || 'client';
    try {
      await assessmentCaseService.addInstrument(caseId, { name, respondent, status: 'pending' });
      await fetchAssessmentCases();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to add instrument');
    }
  };

  const handleRecordInstrumentScore = async (caseId, instrumentIndex, instrumentName) => {
    const total = window.prompt(`Total / summary score for ${instrumentName} (manual entry only):`);
    if (total === null || total === '') return;
    const notes = window.prompt('Score notes (optional):') || '';
    try {
      await assessmentCaseService.recordScore(caseId, instrumentIndex, {
        scores: { total: Number.isNaN(Number(total)) ? total : Number(total) },
        notes,
        status: 'scored',
      });
      await fetchAssessmentCases();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to record score');
    }
  };

  const handleCloseAssessmentCase = async (caseId) => {
    if (!window.confirm('Mark this assessment case complete?')) return;
    try {
      await assessmentCaseService.update(caseId, { status: 'complete' });
      await fetchAssessmentCases();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to close case');
    }
  };

  const handleLogAssessmentTime = async (caseId) => {
    const minutesRaw = window.prompt('Minutes spent (testing / scoring / report prep):');
    if (minutesRaw === null || minutesRaw === '') return;
    const minutes = Number(minutesRaw);
    if (!minutes || minutes <= 0) {
      alert('Enter a positive number of minutes');
      return;
    }
    const activity =
      window.prompt('Activity (testing / scoring / report_prep / feedback):', 'testing') ||
      'testing';
    const notes = window.prompt('Notes (optional):') || '';
    try {
      await assessmentCaseService.addTimeLog(caseId, {
        minutes,
        activity,
        notes,
        createBillingDraft: true,
      });
      alert('Time logged and a billing draft was created for review.');
      await fetchAssessmentCases();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to log time');
    }
  };

  const handleCreateCarePlan = async () => {
    const title = window.prompt('Care plan title:', 'Treatment plan');
    if (!title) return;
    const presentingProblems = window.prompt('Presenting problems (optional):') || '';
    const goal = window.prompt('First goal (optional):') || '';
    try {
      await carePlanService.create({
        patientId: id,
        title,
        presentingProblems,
        status: 'active',
        goals: goal ? [{ description: goal, status: 'in_progress' }] : [],
        interventions: [],
      });
      await fetchCarePlans();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to create care plan');
    }
  };

  const handleAddCarePlanGoal = async (planId) => {
    const description = window.prompt('Goal description:');
    if (!description) return;
    try {
      const plan = carePlans.find((p) => p._id === planId);
      const goals = [...(plan?.goals || []), { description, status: 'in_progress', progressNotes: '' }];
      await carePlanService.update(planId, { goals });
      await fetchCarePlans();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to add goal');
    }
  };

  const handleCreatePortalInvite = async () => {
    const email = window.prompt('Client email for portal invite:');
    if (!email) return;
    try {
      const res = await portalService.createInvite({ patientId: id, email, daysValid: 14 });
      const path = res.data?.portalPath || `/portal/${res.data?.token}`;
      const full = `${window.location.origin}${path}`;
      window.prompt('Share this portal link with the client (copy):', full);
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to create portal invite');
    }
  };

  const handleCreateClientAccount = async () => {
    const email = window.prompt('Client email for login account:');
    if (!email) return;
    try {
      const res = await portalService.createClientAccount({ patientId: id, email });
      const msg = `Account created.\nEmail: ${res.data?.email}\nTemp password: ${res.data?.temporaryPassword || '(existing)'}\nPortal: ${window.location.origin}${res.data?.portalPath || ''}`;
      window.prompt('Share securely (copy):', msg);
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to create client account');
    }
  };

  const handleExportChart = async () => {
    try {
      const res = await practiceExtrasService.exportPatient(id);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `patient-${id}-export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Export failed');
    }
  };

  const handleScribeCapture = async () => {
    const consent = window.confirm('Confirm client consent was captured for scribe/transcription use.');
    if (!consent) return;
    const sourceText = window.prompt('Paste dictation / transcript text:');
    if (!sourceText) return;
    const noteType = window.prompt('Note type (SOAP/DAP/BIRP/progress):', 'SOAP') || 'SOAP';
    try {
      const res = await practiceExtrasService.createScribeDraft({
        patientId: id,
        sourceText,
        noteType,
        consentCaptured: true,
      });
      if (window.confirm('Scribe draft created. Promote to a progress note now?')) {
        await practiceExtrasService.promoteScribe(res.data._id);
        await fetchClinicalNotes();
        alert('Promoted to draft progress note (still labeled AI draft until you sign).');
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Scribe capture failed');
    }
  };

  const handleAddMedication = async () => {
    const name = window.prompt('Medication name:');
    if (!name) return;
    const dose = window.prompt('Dose:') || '';
    const frequency = window.prompt('Frequency:') || '';
    try {
      await practiceExtrasService.addMedication({ patientId: id, name, dose, frequency });
      alert('Medication recorded (structured). eRx/PDMP not enabled.');
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    }
  };

  const handleAddPsychEval = async () => {
    const chiefComplaint = window.prompt('Chief complaint:') || '';
    const mentalStatusExam = window.prompt('MSE notes:') || '';
    const plan = window.prompt('Plan:') || '';
    try {
      await practiceExtrasService.createPsychEval({
        patientId: id,
        chiefComplaint,
        mentalStatusExam,
        plan,
        evalDate: new Date().toISOString(),
      });
      alert('Psychiatry eval draft created.');
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    }
  };

  const handleAddLab = async () => {
    const testName = window.prompt('Lab / test name:');
    if (!testName) return;
    try {
      await practiceExtrasService.createLab({ patientId: id, testName });
      alert('Lab order recorded (manual tracking).');
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    }
  };

  const handleAddChartFact = async (type) => {
    const label = window.prompt(
      type === 'diagnosis'
        ? 'Diagnosis label (e.g. Generalized Anxiety Disorder)'
        : type === 'allergy'
          ? 'Allergy substance'
          : type === 'medication'
            ? 'Medication name'
            : 'Problem / fact label',
    );
    if (!label) return;
    const value =
      window.prompt(
        type === 'diagnosis'
          ? 'Code (optional, e.g. F41.1)'
          : type === 'allergy'
            ? 'Reaction (optional)'
            : type === 'medication'
              ? 'Dose (optional)'
              : 'Details (optional)',
        '',
      ) || '';
    try {
      await chartService.createFact(id, {
        type,
        label,
        value,
        source: 'clinician',
      });
      await fetchChartOverview();
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to add chart fact');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Calculate age if DOB is being changed
    if (name === 'dob' && value) {
      const birthDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      setFormData(prev => ({ ...prev, [name]: value, age: age.toString() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload an image file (JPEG, PNG, or GIF)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      setProfileImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdatePatient = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setUpdateError('');

    try {
      // Split fullName into firstName and lastName for backend
      const nameParts = formData.fullName ? formData.fullName.split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const patientData = {
        ...formData,
        customFields: {
          firstName,
          lastName,
        }
      };

      await patientService.updatePatient(id, patientData);

      // Upload profile picture if selected
      if (profileImage) {
        try {
          await patientService.uploadProfilePicture(id, profileImage);
        } catch (uploadError) {
          console.error('Failed to upload profile picture:', uploadError);
          alert('Patient updated but profile picture upload failed. You can try again later.');
        }
      }

      await fetchPatientDetails();
      setShowEditForm(false);
      setProfileImage(null);
      alert('Patient information updated successfully!');
    } catch (error) {
      console.error('Error updating patient:', error);
      setUpdateError(error.response?.data?.message || 'Failed to update patient information');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateReport = () => {
    navigate(`/intake-form/${id}`);
  };

  const handleEditReport = async (report) => {
    setEditingReport(report);
    try {
      // Fetch report content for editing
      const reportData = await reportService.getReportById(report._id);
      console.log('Report data loaded:', reportData);

      // Handle different possible response structures
      let content = reportData.reportContent || reportData.content || reportData.data?.reportContent || '';
      console.log('Raw content:', content);

      // Convert plain text to HTML formatting for better display
      if (content && !content.includes('<')) {
        // Format the content to look like the PDF
        content = formatReportContentToHTML(content, reportData);
      }

      console.log('Formatted content:', content);
      setEditedReportContent(content);
      setShowEditReportModal(true);
    } catch (error) {
      console.error('Error loading report for editing:', error);
      alert('Failed to load report for editing. Error: ' + (error.message || 'Unknown error'));
    }
  };

  // Format plain text report content to HTML for better display
  const formatReportContentToHTML = (content, reportData) => {
    if (!content) return '';

    let html = '';

    // Process the content line by line
    const lines = content.split('\n');
    let inList = false;
    let skipNextLines = 0;

    for (let i = 0; i < lines.length; i++) {
      if (skipNextLines > 0) {
        skipNextLines--;
        continue;
      }

      const line = lines[i].trim();

      if (!line) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += '<br>';
        continue;
      }

      // Skip duplicate patient information lines
      if (line.includes('PATIENT INFORMATION') ||
          line.includes('Name:') && line.includes('DOB:') ||
          line.includes('Phone:') && line.includes('Email:')) {
        continue;
      }

      // Skip generated date lines
      if (line.includes('Generated:')) {
        continue;
      }

      // Detect headings (lines that are all caps or start with #)
      if (line === line.toUpperCase() && line.length > 5 && !line.match(/^\d+\./) && !line.includes('---')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h3 style="color: #1a5490; margin-top: 20px; margin-bottom: 10px; font-size: 16px;">${line}</h3>`;
      }
      // Detect markdown headers
      else if (line.startsWith('###')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h4 style="color: #1a5490; margin-top: 15px; margin-bottom: 8px;">${line.replace(/^###\s*/, '')}</h4>`;
      }
      else if (line.startsWith('##')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h3 style="color: #1a5490; margin-top: 20px; margin-bottom: 10px;">${line.replace(/^##\s*/, '')}</h3>`;
      }
      else if (line.startsWith('#')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h2 style="color: #1a5490; margin-top: 25px; margin-bottom: 12px;">${line.replace(/^#\s*/, '')}</h2>`;
      }
      // Skip horizontal rules
      else if (line.match(/^-{3,}$/)) {
        continue;
      }
      // Detect bullet points or numbered lists
      else if (line.match(/^[-*•]\s/) || line.match(/^\d+\.\s/)) {
        if (!inList) {
          html += '<ul style="margin: 10px 0; padding-left: 30px;">';
          inList = true;
        }
        html += `<li style="margin: 5px 0; line-height: 1.6;">${line.replace(/^[-*•]\s/, '').replace(/^\d+\.\s/, '')}</li>`;
      }
      // Handle bold markdown **text**
      else if (line.match(/\*\*(.*?)\*\*/)) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        html += `<p style="margin: 10px 0; line-height: 1.6;">${formatted}</p>`;
      }
      // Regular paragraph
      else {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<p style="margin: 10px 0; line-height: 1.6; text-align: justify;">${line}</p>`;
      }
    }

    if (inList) {
      html += '</ul>';
    }

    return html;
  };

  const handleSaveEditedReport = async () => {
    if (!editingReport) return;

    setIsSavingReport(true);
    try {
      // The content is already in HTML format from ReactQuill
      // Save it and regenerate the PDF
      await reportService.updateReport(editingReport._id, {
        reportContent: editedReportContent
      });

      // Refresh reports
      await fetchPatientReports();

      // Close modal
      setShowEditReportModal(false);
      setEditingReport(null);
      setEditedReportContent('');

      alert('Report updated and PDF regenerated successfully!');
    } catch (error) {
      console.error('Error updating report:', error);
      alert('Failed to update report. Error: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setIsSavingReport(false);
    }
  };

  const handleDownloadEditedReport = async () => {
    if (!editingReport) return;

    try {
      await reportService.downloadReport(editingReport._id);
    } catch (error) {
      console.error('Error downloading edited report:', error);
      alert('Failed to download report');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'completed':
      case 'locked':
      case 'signed':
        return '#10B981';
      case 'in_review':
        return '#3B82F6';
      case 'draft':
        return '#F59E0B';
      case 'pending':
      case 'generating':
        return '#F59E0B';
      case 'failed':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const getClinicalLabel = (report) => {
    if (report.clinicalStatus === 'locked' || report.clinicalStatus === 'signed') {
      return 'Signed & Locked';
    }
    if (report.clinicalStatus === 'in_review') {
      return 'In Review';
    }
    if (report.isAiDraft !== false || report.clinicalStatus === 'draft' || !report.clinicalStatus) {
      return 'AI Draft';
    }
    return report.clinicalStatus || report.status || 'Draft';
  };

  const isReportLocked = (report) =>
    report.clinicalStatus === 'locked' || report.clinicalStatus === 'signed';

  const handleSubmitForReview = async (report) => {
    try {
      await reportService.submitForReview(report._id);
      await fetchPatientReports();
      alert('Report submitted for clinical review.');
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to submit for review');
    }
  };

  const handleSignAndLock = async (report) => {
    if (!window.confirm('Sign and lock this report? Content will become immutable.')) {
      return;
    }
    try {
      await reportService.signAndLock(report._id);
      await fetchPatientReports();
      alert('Report signed and locked successfully.');
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to sign report');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US');
  };

  if (loading) {
    return (
      <Layout>
        <div className="patient-detail-loading">
          <div className="loading-spinner"></div>
          <p>Loading patient details...</p>
        </div>
      </Layout>
    );
  }

  if (error || !patient) {
    return (
      <Layout>
        <div className="patient-detail-error">
          <h2>Error</h2>
          <p>{error || 'Patient not found'}</p>
          <button onClick={() => navigate('/patients')} className="btn-back">
            Back to Patients
          </button>
        </div>
      </Layout>
    );
  }

  if (showEditForm) {
    return (
      <Layout>
        <div className="patient-form-container">
          <div className="form-header">
            <h1>Edit Patient</h1>
          </div>

          <form onSubmit={handleUpdatePatient} className="patient-form">
            {/* Patient Information Section */}
            <div className="form-section">
              <h2 className="section-title">Patient Information</h2>

              {/* Profile Image Upload */}
              <div className="profile-upload-section">
                <label className="form-label">Profile Image</label>
                <div className="profile-upload-container">
                  <div className="profile-preview">
                    {profilePreview ? (
                      <img src={profilePreview} alt="Profile" />
                    ) : (
                      <div className="profile-placeholder">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                          <path d="M20 20C24.42 20 28 16.42 28 12C28 7.58 24.42 4 20 4C15.58 4 12 7.58 12 12C12 16.42 15.58 20 20 20Z" fill="#E0E0E0"/>
                          <path d="M20 24C13.33 24 4 27.33 4 34V36H36V34C36 27.33 26.67 24 20 24Z" fill="#E0E0E0"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose Image
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                    className={`form-input ${errors.fullName ? 'error' : ''}`}
                    required
                  />
                  {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Phone Number <span className="required">*</span>
                  </label>
                  <div className="phone-input-wrapper">
                    <span className="phone-prefix">📞 +1</span>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="000-000-0000"
                      className={`form-input with-prefix ${errors.phoneNumber ? 'error' : ''}`}
                    />
                  </div>
                  {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    className={`form-input ${errors.email ? 'error' : ''}`}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    DOB <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className={`form-input ${errors.dob ? 'error' : ''}`}
                  />
                  {errors.dob && <span className="error-message">{errors.dob}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder="Age"
                    className="form-input"
                    readOnly
                  />
                  <span className="text-xs text-gray-500">Auto-calculated from DOB</span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Gender <span className="required">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className={`form-select ${errors.gender ? 'error' : ''}`}
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                  {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Blood Group</label>
                  <select
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Emergency Contact</label>
                  <input
                    type="tel"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    placeholder="Emergency Contact Number"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Primary Language</label>
                  <input
                    type="text"
                    name="primaryLanguage"
                    value={formData.primaryLanguage}
                    onChange={handleInputChange}
                    placeholder="e.g., English"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Race/Ethnicity</label>
                  <input
                    type="text"
                    name="raceEthnicity"
                    value={formData.raceEthnicity}
                    onChange={handleInputChange}
                    placeholder="Race/Ethnicity"
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="form-section">
              <h2 className="section-title">Address Information</h2>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Street Address</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    placeholder="Street Address"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="State"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">ZIP Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="ZIP Code"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Country"
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {updateError && (
              <div className="error-message">
                {updateError}
              </div>
            )}

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowEditForm(false);
                  setProfileImage(null);
                  setProfilePreview(patient?.profileImage || null);
                }}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Update Patient'}
              </button>
            </div>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="patient-detail-container">
        <div className="patient-profile-section">
          <div className="section-header">
            <h2>Patient Profile</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={handleCreatePortalInvite} className="btn-edit-profile">
                Portal Invite
              </button>
              <button onClick={handleCreateClientAccount} className="btn-edit-profile">
                Client Login
              </button>
              <button onClick={handleExportChart} className="btn-edit-profile">
                Export Chart
              </button>
              <button onClick={handleScribeCapture} className="btn-edit-profile">
                AI Scribe
              </button>
              <button onClick={handleAddMedication} className="btn-edit-profile">
                + Med
              </button>
              <button onClick={handleAddPsychEval} className="btn-edit-profile">
                Psych Eval
              </button>
              <button onClick={handleAddLab} className="btn-edit-profile">
                + Lab
              </button>
              <button onClick={() => setShowEditForm(true)} className="btn-edit-profile">
                Edit Profile
              </button>
            </div>
          </div>

          <div className="patient-info-card">
            <h3>Patient Information</h3>

            <div className="patient-main-info">
              {patient.profileImage ? (
                <img
                  src={patient.profileImage}
                  alt={patient.fullName}
                  className="patient-avatar"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="patient-avatar-placeholder"
                style={{ display: patient.profileImage ? 'none' : 'flex' }}
              >
                {patient.fullName?.split(' ').map(n => n[0]).join('').toUpperCase()}
              </div>

              <div className="patient-name-address">
                <h4>{patient.fullName}</h4>
                <p>{patient.street || patient.address || ''}</p>
                <p>{[patient.city, patient.state, patient.country].filter(Boolean).join(', ')}</p>
              </div>
            </div>

            <div className="patient-details-grid">
              <div className="detail-item">
                <span className="detail-label">Sex:</span>
                <span className="detail-value">{patient.gender || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">DOB:</span>
                <span className="detail-value">{formatDate(patient.dob)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Age:</span>
                <span className="detail-value">{patient.age || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Blood Type:</span>
                <span className="detail-value">{patient.bloodGroup || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone No:</span>
                <span className="detail-value">{patient.phoneNumber || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Primary Language:</span>
                <span className="detail-value">{patient.primaryLanguage || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Race/Ethnicity:</span>
                <span className="detail-value">{patient.raceEthnicity || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Registered Date:</span>
                <span className="detail-value">{formatDate(patient.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="reports-section">
          <div className="section-header">
            <h2>Overall Report</h2>
            <button onClick={handleCreateReport} className="btn-create-report">
              Create Report
            </button>
          </div>

          <div className="reports-table-container">
            {reports.length > 0 ? (
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Report Number</th>
                    <th>Type</th>
                    <th>Date Created</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report, index) => (
                    <tr key={report._id}>
                      <td>Clinical Psychology Report {index + 1}</td>
                      <td>{report.reportType || 'Assessment'}</td>
                      <td>{formatDate(report.createdAt)}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(report.clinicalStatus || report.status) }}
                        >
                          {getClinicalLabel(report)}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => reportService.viewReport(report._id)}
                          className="btn-view-report"
                          style={{ marginRight: '8px' }}
                        >
                          View
                        </button>
                        {!isReportLocked(report) && (
                          <button
                            onClick={() => handleEditReport(report)}
                            className="btn-edit-report"
                            style={{ marginRight: '8px' }}
                          >
                            Edit
                          </button>
                        )}
                        {!isReportLocked(report) && report.clinicalStatus !== 'in_review' && (
                          <button
                            onClick={() => handleSubmitForReview(report)}
                            className="btn-edit-report"
                            style={{ marginRight: '8px' }}
                          >
                            Submit Review
                          </button>
                        )}
                        {!isReportLocked(report) && (
                          <button
                            onClick={() => handleSignAndLock(report)}
                            className="btn-edit-report"
                            style={{ marginRight: '8px' }}
                          >
                            Sign & Lock
                          </button>
                        )}
                        <button
                          onClick={() => reportService.downloadReport(report._id)}
                          className="btn-download-report"
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-reports">
                <p>No reports available for this patient</p>
                <button onClick={handleCreateReport} className="btn-create-first-report">
                  Create First Report
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Chart Overview */}
        <div className="reports-section" style={{ marginTop: '2rem' }}>
          <div className="section-header">
            <h2>Chart Overview</h2>
            <button
              className="btn-create-report"
              type="button"
              onClick={async () => {
                try {
                  const res = await aiService.chartSummary({ patientId: id });
                  const text = res.data?.summary || '';
                  window.alert(
                    `${res.data?.disclaimer || 'AI summary'}\n\n${text.slice(0, 3500)}`,
                  );
                } catch (e) {
                  alert(e.response?.data?.message || e.message || 'AI summary failed');
                }
              }}
            >
              AI chart summary
            </button>
          </div>
          {chartOverview?.byType ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              {['allergy', 'medication', 'diagnosis', 'goal', 'risk', 'history'].map((type) => (
                <div key={type}>
                  <strong style={{ textTransform: 'capitalize' }}>{type}s</strong>
                  <ul>
                    {(chartOverview.byType[type] || []).length === 0 && <li>None recorded</li>}
                    {(chartOverview.byType[type] || []).map((fact) => (
                      <li key={fact._id}>
                        {fact.label}
                        {fact.value ? ` — ${fact.value}` : ''}
                        {fact.verificationState === 'clinician_verified' ? ' ✓' : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p>No chart data yet.</p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <button className="btn-create-report" onClick={() => handleAddChartFact('allergy')}>+ Allergy</button>
            <button className="btn-create-report" onClick={() => handleAddChartFact('medication')}>+ Medication</button>
            <button className="btn-create-report" onClick={() => handleAddChartFact('diagnosis')}>+ Diagnosis</button>
            <button className="btn-create-report" onClick={() => handleAddChartFact('goal')}>+ Goal</button>
            <button className="btn-create-report" onClick={() => handleAddChartFact('risk')}>+ Risk</button>
            <button className="btn-create-report" onClick={proposeIntakeFacts}>AI: intake → chart facts</button>
          </div>
        </div>

        {/* Client ledger (TheraNest-style on-chart) */}
        <div className="reports-section" style={{ marginTop: '2rem' }}>
          <div className="section-header">
            <h2>Client ledger</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-create-report" onClick={printStatement}>
                Statement PDF
              </button>
              <button type="button" className="btn-create-report" onClick={fetchLedger}>
                Refresh
              </button>
            </div>
          </div>
          {!ledger ? (
            <p>No ledger data yet.</p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Open balance</div>
                  <div style={{ fontSize: 22, fontWeight: 600 }}>
                    ${((ledger.balanceCents || 0) / 100).toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>In progress</div>
                  <div style={{ fontSize: 22, fontWeight: 600 }}>
                    {(ledger.invoices || []).filter((i) =>
                      ['draft', 'sent', 'partial', 'open'].includes(i.status),
                    ).length}
                  </div>
                </div>
              </div>
              {(ledger.invoices || []).length === 0 ? (
                <p>No invoices for this client.</p>
              ) : (
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>Balance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ledger.invoices || []).map((inv) => {
                      const bal =
                        inv.balanceDueCents ??
                        Math.max(0, (inv.amountCents || 0) - (inv.amountPaidCents || 0));
                      return (
                        <tr key={inv._id}>
                          <td>{inv.invoiceNumber}</td>
                          <td>{inv.status}</td>
                          <td>${((inv.amountCents || 0) / 100).toFixed(2)}</td>
                          <td>${(bal / 100).toFixed(2)}</td>
                          <td>
                            {bal > 0 && (
                              <>
                                <button
                                  type="button"
                                  className="btn-view-report"
                                  style={{ marginRight: 8 }}
                                  disabled={payBusy}
                                  onClick={() => handleAcceptPayment(inv)}
                                >
                                  Accept payment
                                </button>
                                <button
                                  type="button"
                                  className="btn-view-report"
                                  disabled={payBusy}
                                  onClick={() => handlePayLink(inv)}
                                >
                                  Pay link
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {/* Clinical Notes */}
        <div className="reports-section" style={{ marginTop: '2rem' }}>
          <div className="section-header">
            <h2>Progress Notes</h2>
            <button
              className="btn-create-report"
              onClick={() => {
                setEditingNote(null);
                setNoteForm(emptyNoteForm);
                setShowNoteModal(true);
              }}
            >
              New Note
            </button>
          </div>
          {clinicalNotes.length === 0 ? (
            <p>No progress notes yet.</p>
          ) : (
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clinicalNotes.map((note) => (
                  <tr key={note._id}>
                    <td>{note.title || 'Untitled'}</td>
                    <td>{note.noteType}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(note.clinicalStatus) }}
                      >
                        {note.clinicalStatus}
                      </span>
                    </td>
                    <td>{formatDate(note.createdAt)}</td>
                    <td>
                      {note.clinicalStatus === 'draft' && (
                        <>
                          <button
                            className="btn-view-report"
                            style={{ marginRight: 8 }}
                            onClick={() => {
                              const s = note.sections || {};
                              setEditingNote(note);
                              setNoteForm({
                                noteType: note.noteType || 'SOAP',
                                title: note.title || '',
                                subjective: s.subjective || '',
                                objective: s.objective || '',
                                assessment: s.assessment || '',
                                plan: s.plan || '',
                                data: s.data || '',
                                intervention: s.intervention || '',
                                response: s.response || '',
                                behavior: s.behavior || '',
                                content: note.content || '',
                              });
                              setShowNoteModal(true);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-view-report"
                            onClick={() => handleSignNote(note._id)}
                          >
                            Sign &amp; Lock
                          </button>
                        </>
                      )}
                      {note.clinicalStatus === 'locked' && (
                        <span style={{ color: '#666', fontSize: 13 }}>Immutable</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {clinicalNotes.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              {clinicalNotes.map((note) => (
                <div key={`${note._id}-comments`} style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>
                    Comments · {note.title || 'Untitled'}
                  </div>
                  <CommentThread
                    targetType="clinical_note"
                    targetId={note._id}
                    patientId={id}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assessment Cases */}
        <div className="reports-section" style={{ marginTop: '2rem' }}>
          <div className="section-header">
            <h2>Assessment Cases</h2>
            <button className="btn-create-report" onClick={handleCreateAssessmentCase}>
              New Case
            </button>
          </div>
          {assessmentCases.length === 0 ? (
            <p>No assessment cases yet.</p>
          ) : (
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Instruments</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assessmentCases.map((c) => (
                  <tr key={c._id}>
                    <td>
                      {c.title}
                      {c.referralQuestion ? (
                        <div style={{ fontSize: 12, color: '#666' }}>{c.referralQuestion}</div>
                      ) : null}
                    </td>
                    <td>{c.status}</td>
                    <td>
                      {(c.instruments || []).length === 0
                        ? '—'
                        : (c.instruments || []).map((inst, idx) => (
                            <div key={idx} style={{ marginBottom: 4 }}>
                              {inst.name}
                              {inst.scores?.total != null ? ` (score: ${inst.scores.total})` : ''}
                              {!['complete', 'cancelled'].includes(c.status) && (
                                <button
                                  className="btn-view-report"
                                  style={{ marginLeft: 6, fontSize: 11 }}
                                  onClick={() => handleRecordInstrumentScore(c._id, idx, inst.name)}
                                >
                                  Score
                                </button>
                              )}
                            </div>
                          ))}
                    </td>
                    <td>{formatDate(c.createdAt)}</td>
                    <td>
                      {!['complete', 'cancelled'].includes(c.status) && (
                        <>
                          <button
                            className="btn-view-report"
                            style={{ marginRight: 8 }}
                            onClick={() => handleAddMeasure(c._id)}
                          >
                            + Instrument
                          </button>
                          <button
                            className="btn-view-report"
                            style={{ marginRight: 8 }}
                            onClick={() => handleLogAssessmentTime(c._id)}
                          >
                            Log time
                          </button>
                          <button
                            className="btn-view-report"
                            onClick={() => handleCloseAssessmentCase(c._id)}
                          >
                            Complete
                          </button>
                        </>
                      )}
                      {(c.timeLogs || []).length > 0 && (
                        <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                          {(c.timeLogs || []).reduce((sum, t) => sum + (t.minutes || 0), 0)} min logged
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Care Plans */}
        <div className="reports-section" style={{ marginTop: '2rem' }}>
          <div className="section-header">
            <h2>Care Plans</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-create-report"
                onClick={async () => {
                  try {
                    const noteText = clinicalNotes
                      .slice(0, 3)
                      .map((n) => n.content || '')
                      .join('\n\n');
                    if (!noteText.trim()) {
                      alert('Add a progress note first so AI can draft from it.');
                      return;
                    }
                    const res = await aiService.carePlanDraft({
                      noteText,
                      patientId: id,
                    });
                    const d = res.data || {};
                    const goals = (d.goals || []).map((g) =>
                      typeof g === 'string' ? { description: g } : g,
                    );
                    if (
                      !window.confirm(
                        `AI care-plan draft (must approve):\n\n${(d.summary || d.title || '')}\n\nGoals:\n${goals
                          .map((g) => `- ${g.description || g.text || JSON.stringify(g)}`)
                          .join('\n')}\n\n${d.disclaimer || ''}\n\nCreate as draft care plan?`,
                      )
                    ) {
                      return;
                    }
                    await carePlanService.create({
                      patientId: id,
                      title: d.title || 'AI draft care plan',
                      status: 'draft',
                      goals,
                      notes: `${d.summary || ''}\n\n[AI draft — clinician must approve]`,
                    });
                    await fetchCarePlans();
                  } catch (e) {
                    alert(e.response?.data?.message || e.message);
                  }
                }}
              >
                AI draft from notes
              </button>
              <button className="btn-create-report" onClick={handleCreateCarePlan}>
                New Care Plan
              </button>
            </div>
          </div>
          {carePlans.length === 0 ? (
            <p>No care plans yet.</p>
          ) : (
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Goals</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {carePlans.map((plan) => (
                  <tr key={plan._id}>
                    <td>
                      {plan.title}
                      {plan.presentingProblems ? (
                        <div style={{ fontSize: 12, color: '#666' }}>{plan.presentingProblems}</div>
                      ) : null}
                    </td>
                    <td>{plan.status}</td>
                    <td>
                      {(plan.goals || []).length === 0
                        ? '—'
                        : (plan.goals || []).map((g, i) => (
                            <div key={i}>{g.description} [{g.status}]</div>
                          ))}
                    </td>
                    <td>{formatDate(plan.createdAt)}</td>
                    <td>
                      {plan.status !== 'completed' && plan.status !== 'discontinued' && (
                        <button
                          className="btn-view-report"
                          onClick={() => handleAddCarePlanGoal(plan._id)}
                        >
                          + Goal
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* Clinical Note Modal */}
      {showNoteModal && (
        <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h2>{editingNote ? 'Edit Progress Note' : 'New Progress Note'}</h2>
              <button className="modal-close" onClick={() => setShowNoteModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label>
                Note Type
                <select
                  value={noteForm.noteType}
                  onChange={(e) => setNoteForm({ ...noteForm, noteType: e.target.value })}
                  style={{ width: '100%', marginTop: 4 }}
                >
                  <option value="SOAP">SOAP</option>
                  <option value="DAP">DAP</option>
                  <option value="BIRP">BIRP</option>
                  <option value="progress">Progress</option>
                  <option value="intake">Intake</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                Title
                <input
                  type="text"
                  value={noteForm.title}
                  onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </label>
              {noteForm.noteType === 'SOAP' && (
                <>
                  <label>Subjective<textarea rows={2} value={noteForm.subjective} onChange={(e) => setNoteForm({ ...noteForm, subjective: e.target.value })} style={{ width: '100%' }} /></label>
                  <label>Objective<textarea rows={2} value={noteForm.objective} onChange={(e) => setNoteForm({ ...noteForm, objective: e.target.value })} style={{ width: '100%' }} /></label>
                  <label>Assessment<textarea rows={2} value={noteForm.assessment} onChange={(e) => setNoteForm({ ...noteForm, assessment: e.target.value })} style={{ width: '100%' }} /></label>
                  <label>Plan<textarea rows={2} value={noteForm.plan} onChange={(e) => setNoteForm({ ...noteForm, plan: e.target.value })} style={{ width: '100%' }} /></label>
                </>
              )}
              {noteForm.noteType === 'DAP' && (
                <>
                  <label>Data<textarea rows={2} value={noteForm.data} onChange={(e) => setNoteForm({ ...noteForm, data: e.target.value })} style={{ width: '100%' }} /></label>
                  <label>Assessment<textarea rows={2} value={noteForm.assessment} onChange={(e) => setNoteForm({ ...noteForm, assessment: e.target.value })} style={{ width: '100%' }} /></label>
                  <label>Plan<textarea rows={2} value={noteForm.plan} onChange={(e) => setNoteForm({ ...noteForm, plan: e.target.value })} style={{ width: '100%' }} /></label>
                </>
              )}
              {noteForm.noteType === 'BIRP' && (
                <>
                  <label>Behavior<textarea rows={2} value={noteForm.behavior} onChange={(e) => setNoteForm({ ...noteForm, behavior: e.target.value })} style={{ width: '100%' }} /></label>
                  <label>Intervention<textarea rows={2} value={noteForm.intervention} onChange={(e) => setNoteForm({ ...noteForm, intervention: e.target.value })} style={{ width: '100%' }} /></label>
                  <label>Response<textarea rows={2} value={noteForm.response} onChange={(e) => setNoteForm({ ...noteForm, response: e.target.value })} style={{ width: '100%' }} /></label>
                  <label>Plan<textarea rows={2} value={noteForm.plan} onChange={(e) => setNoteForm({ ...noteForm, plan: e.target.value })} style={{ width: '100%' }} /></label>
                </>
              )}
              {!['SOAP', 'DAP', 'BIRP'].includes(noteForm.noteType) && (
                <label>Content<textarea rows={4} value={noteForm.content} onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })} style={{ width: '100%' }} /></label>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-cancel" onClick={() => setShowNoteModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleSaveNote}>
                {editingNote ? 'Save Changes' : 'Create Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Report Modal */}
      {showEditReportModal && editingReport && (
        <div className="modal-overlay" onClick={() => setShowEditReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {isReportLocked(editingReport) ? 'View Signed Report' : 'Edit AI Draft Report'}
              </h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowEditReportModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {editingReport.isAiDraft !== false && !isReportLocked(editingReport) && (
                <p style={{ marginBottom: '12px', color: '#92400E', background: '#FEF3C7', padding: '8px 12px', borderRadius: '8px' }}>
                  This is an AI-generated draft. A licensed clinician must review and sign before it becomes a locked clinical record.
                </p>
              )}
              <div className="form-group">
                <ReactQuill
                  value={editedReportContent}
                  onChange={setEditedReportContent}
                  theme="snow"
                  readOnly={isReportLocked(editingReport)}
                  modules={{
                    toolbar: isReportLocked(editingReport) ? false : [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'indent': '-1'}, { 'indent': '+1' }],
                      [{ 'align': [] }],
                      [{ 'color': [] }, { 'background': [] }],
                      ['clean']
                    ]
                  }}
                  formats={[
                    'header',
                    'bold', 'italic', 'underline',
                    'list', 'bullet', 'indent',
                    'align', 'color', 'background'
                  ]}
                  placeholder="Loading report content..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowEditReportModal(false)}
                disabled={isSavingReport}
              >
                Cancel
              </button>
              <button
                className="btn-download-report"
                onClick={handleDownloadEditedReport}
                style={{ marginRight: '8px' }}
                disabled={isSavingReport}
              >
                Download Current PDF
              </button>
              {!isReportLocked(editingReport) && (
                <button
                  className="btn-primary"
                  onClick={handleSaveEditedReport}
                  disabled={isSavingReport}
                >
                  {isSavingReport ? 'Saving & Generating PDF...' : 'Save Draft & Regenerate PDF'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PatientDetail;